import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoNozzle,
} from 'types'

import { FeatureBoltRing3D } from './procedural/FeatureBoltRing3D'
import { FeatureCylinder3D } from './procedural/FeatureCylinder3D'
import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import { SurfaceFeature3D } from './SurfaceFeature3D'

const threshold = GeckoMeasurement.CreateFromValue(
  10,
  GeckoMeasurementUnit.Inches
)

const getScale = (radius: GeckoMeasurement) => {
  if (radius.gte(threshold)) return 1
  return radius.toNumber(threshold.unit) / threshold.toNumber(threshold.unit)
}

const getRepadRadius = (radius: GeckoMeasurement, scale: number) => {
  const padding = GeckoMeasurement.CreateFromValue(0.1, radius.unit).scale(
    scale
  )
  return radius.plus(padding)
}

const getFlangeRadius = (radius: GeckoMeasurement, scale: number) => {
  const padding = GeckoMeasurement.CreateFromValue(0.025, radius.unit).scale(
    scale
  )
  return radius.plus(padding)
}

const getRepadThickness = (scale: number) => {
  const padding = GeckoMeasurement.CreateFromValue(
    0.01,
    GeckoMeasurementUnit.Meters
  ).scale(scale)
  return padding
}

const getProtrusion = (scale: number) => {
  return 0.25 * scale
}

/**
 * A component representing a nozzle on the surface of a component
 * @param model             The nozzle model (from the asset model)
 * @constructor
 */
export const NozzleFeature3D: React.FC<{
  model: GeckoNozzle
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()
  const scale = React.useMemo(() => getScale(model.radius), [model.radius])
  const flangeRadius = React.useMemo(
    () => getFlangeRadius(model.radius, scale),
    [model.radius, scale]
  )
  const repadRadius = React.useMemo(
    () => getRepadRadius(model.radius, scale),
    [model.radius, scale]
  )

  const repadThickness = React.useMemo(() => getRepadThickness(scale), [scale])

  const protrusion = React.useMemo(() => getProtrusion(scale), [scale])

  //----------------------------------------------------------------------------

  const { labelCoords, positionLabelAbove } = React.useMemo(() => {
    const x = model.geometry.x
    let y = model.geometry.y
      .plus(repadRadius)
      .plus(GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches))
    const positionAbove =
      bounds.y.end.minus(y).num > bounds.height.scale(1 / 10).num
    if (!positionAbove) {
      y = model.geometry.y.minus(repadRadius)
    }
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelAbove: positionAbove }
  }, [model, repadRadius, bounds])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelAbove ? 'bottom' : 'top'}
      />
      <SurfaceFeature3D model={model}>
        {/* Repad */}
        <FeatureCylinder3D
          feature={model}
          position={[0, 0, 0]}
          radius={repadRadius.num}
          height={repadThickness.num}
          debugColor={'blue'}
        />
        {/* Tube */}
        <FeatureCylinder3D
          feature={model}
          position={[0, repadThickness.num + 0.01 * scale, 0]}
          radius={model.radius.num}
          height={protrusion - 0.025 * scale - repadThickness.num}
          debugColor={'purple'}
        />
        {/* Flange */}
        <FeatureCylinder3D
          feature={model}
          position={[0, protrusion - 0.03 * scale, 0]}
          radius={flangeRadius.num}
          height={0.025 * scale}
          debugColor={'red'}
        />
        {/* Cylinder on top of flange to fake hollow-ness */}
        <FeatureCylinder3D
          feature={model}
          position={[0, protrusion - 0.02 * scale, 0]}
          radius={model.radius.num}
          height={0.02 * scale}
          debugColor={'yellow'}
        />
        {/* Flange bolts */}
        <FeatureBoltRing3D
          feature={model}
          position={[0, protrusion, 0]}
          radius={model.radius}
          scale={scale}
        />
      </SurfaceFeature3D>
    </>
  )
}
